import React, { useState, useRef, useCallback, useEffect } from 'react'
import { ListGroup, Badge, Button } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons'
import Search from 'shared/forms/search'
import { filter, uniqBy } from 'lodash'
import { useVendors } from 'api/hooks/use-vendors'
import { useQueryClient } from 'react-query'
import { useBulkCreateDashboardVendors, useBulkDestroyDashboardVendors } from 'api/hooks/use-dashboard-vendors'
import Table from 'react-bootstrap/Table'
import { pick } from 'lodash'
import VendorItem from './item'
import DeleteVendor from './delete'
import UpdateVendor from './update'
import Spinner from 'react-bootstrap/Spinner'

function VendorSearch({ params, setParams, canEdit = true, showAsAccordion = false, isRequired = false, label = 'Vendor(s)', initialSearch = '' }) {
  const vendors = params.vendors || []
  const excludedVendors = params.excludedVendors || []
  const allVendors = [...vendors, ...excludedVendors]
  const [isExpanded, setIsExpanded] = useState(isRequired)
  const queryClient = useQueryClient()
  const { mutate: bulkCreateVendors } = useBulkCreateDashboardVendors({
    options: {
      onSuccess: (data) => {
        // Update local state with the dashboard_vendor_ids from the response
        setParams((prev) => {
          const updatedVendors = (prev.vendors || []).map((v) => {
            const created = data.find((d) => d.vendor_id === v.id)
            return created ? { ...v, dashboard_vendor_id: created.id } : v
          })
          return { ...prev, vendors: updatedVendors }
        })
        queryClient.invalidateQueries(`dashboards/${params.dashboardId}`)
      }
    }
  })
  const { mutate: bulkDestroyVendors } = useBulkDestroyDashboardVendors({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries(`dashboards/${params.dashboardId}`)
      }
    }
  })
  const defaultSearchParams = {
    search: '',
    section: params.section_id,
    page: 1,
    perPage: 10
  }
  const [searchParams, setSearchParams] = useState(
    initialSearch
      ? { ...defaultSearchParams, search: initialSearch }
      : defaultSearchParams
  )
  const [loadAll, setLoadAll] = useState(false)
  const [accumulatedVendors, setAccumulatedVendors] = useState([])
  const [hasMoreResults, setHasMoreResults] = useState(true)
  const listRef = useRef(null)

  // Apply initialSearch when it changes
  useEffect(() => {
    if (initialSearch) {
      setAccumulatedVendors([])
      setHasMoreResults(true)
      setSearchParams((prev) => ({ ...prev, search: initialSearch, page: 1 }))
    }
  }, [initialSearch])

  // Reset loadAll when search term changes
  useEffect(() => {
    setLoadAll(false)
  }, [searchParams.search])

  const addOrRemoveVendor = (vendor, dashboardVendorId = null) => {
    const findVendor = filter(allVendors, {
      id: vendor.id
    })
    if (findVendor.length > 0) {
      // Remove vendor from both arrays
      const newVendors = vendors.filter(v => v.id !== vendor.id)
      const newExcludedVendors = excludedVendors.filter(v => v.id !== vendor.id)
      setParams((prev) => ({
        ...prev,
        vendors: newVendors,
        excludedVendors: newExcludedVendors
      }))
      queryClient.invalidateQueries('transactions')
    } else {
      // Add vendor to included vendors by default
      const newVendor = {
        ...pick(vendor, ['id', 'name', 'external_id']),
        options: 'include',
        dashboard_vendor_id: dashboardVendorId
      }
      setParams((prev) => ({
        ...prev,
        vendors: [...vendors, newVendor]
      }))
      queryClient.invalidateQueries('transactions')
    }
  }

  const updateVendorWithDashboardId = (vendorId, dashboardVendorId) => {
    setParams((prev) => {
      const updatedVendors = prev.vendors.map(v =>
        v.id === vendorId ? { ...v, dashboard_vendor_id: dashboardVendorId } : v
      )
      const updatedExcludedVendors = prev.excludedVendors.map(v =>
        v.id === vendorId ? { ...v, dashboard_vendor_id: dashboardVendorId } : v
      )
      return {
        ...prev,
        vendors: updatedVendors,
        excludedVendors: updatedExcludedVendors
      }
    })
  }

  const toggleIncludeExclude = (vendor, options) => {
    // Remove vendor from both arrays first
    const newVendors = vendors.filter(v => v.id !== vendor.id)
    const newExcludedVendors = excludedVendors.filter(v => v.id !== vendor.id)
    
    // Update vendor options and add to appropriate array
    const updatedVendor = { ...vendor, options: options }
    
    if (options === 'include') {
      setParams((prev) => ({
        ...prev,
        vendors: [...newVendors, updatedVendor],
        excludedVendors: newExcludedVendors
      }))
    } else {
      setParams((prev) => ({
        ...prev,
        vendors: newVendors,
        excludedVendors: [...newExcludedVendors, updatedVendor]
      }))
    }
    
    queryClient.invalidateQueries('vendors')
    queryClient.invalidateQueries('time_periods')
  }


  const selectedVendorIds = allVendors.map(v => v.id)

  // Clear accumulated vendors when selections change so API re-fetches with updated exclude_ids
  useEffect(() => {
    setAccumulatedVendors([])
    setSearchParams(prev => ({ ...prev, page: 1 }))
  }, [selectedVendorIds.length])

  const { isLoading, isFetching, data } = useVendors({
    params: {
      ...searchParams,
      exclude_ids: selectedVendorIds
    },
    options: {
      enabled: searchParams.search !== '' && searchParams.section.length > 2,
      onSuccess: (responseData) => {
        if (searchParams.page === 1) {
          setAccumulatedVendors(responseData.data)
        } else {
          setAccumulatedVendors(prev => uniqBy([...prev, ...responseData.data], 'id'))
        }
        const totalPages = responseData.meta?.pages || 1
        setHasMoreResults(searchParams.page < totalPages)
      }
    }
  })

  // Reset pagination when search changes
  const searchParamsRef = useRef(searchParams)
  searchParamsRef.current = searchParams
  const handleSearchParamsChange = useCallback((newParams) => {
    if (typeof newParams === 'function') {
      setSearchParams(prev => {
        const updated = newParams(prev)
        if (updated.search !== prev.search) {
          setAccumulatedVendors([])
          setHasMoreResults(true)
          return { ...updated, page: 1 }
        }
        return updated
      })
    } else {
      if (newParams.search !== searchParamsRef.current.search) {
        setAccumulatedVendors([])
        setHasMoreResults(true)
        setSearchParams({ ...newParams, page: 1 })
      } else {
        setSearchParams(newParams)
      }
    }
  }, [])

  // Handle scroll for infinite pagination
  const handleScroll = useCallback((event) => {
    const target = event.target
    const scrolledToBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 50

    if (scrolledToBottom && !isFetching && hasMoreResults) {
      setSearchParams(prev => ({ ...prev, page: prev.page + 1 }))
    }
  }, [isFetching, hasMoreResults])

  const displayVendors = accumulatedVendors.length > 0 ? accumulatedVendors : (data?.data || [])

  // Format vendor value for display
  const formatVendorValue = () => {
    const includedCount = vendors.length
    const excludedCount = excludedVendors.length

    if (includedCount === 0 && excludedCount === 0) {
      return 'Not set'
    }

    const parts = []
    if (includedCount > 0) {
      parts.push(`${includedCount} included`)
    }
    if (excludedCount > 0) {
      parts.push(`${excludedCount} excluded`)
    }
    return parts.join(', ')
  }

  const content = (
    <div>
      <Search
        params={searchParams}
        setParams={handleSearchParamsChange}
        placeholder={'Search Vendor'}
        disabled={!canEdit}
      />
      <ListGroup
        variant="flush"
        className="border border-light mt-2"
        ref={listRef}
        onScroll={handleScroll}
        style={{ maxHeight: '300px', overflowY: 'auto' }}
      >
        {isLoading && searchParams.page === 1 && (
          <ListGroup.Item>
            <div className="text-muted text-center">
              <Spinner animation="grow" variant="warning" className="my-5" />
            </div>
          </ListGroup.Item>
        )}
        {displayVendors.length > 0 &&
          displayVendors.map((v) => (
            <VendorItem
              key={`vendor-option-${v.id}`}
              vendor={v}
              params={params}
              dashboardId={params.dashboardId}
              addOrRemoveVendor={addOrRemoveVendor}
              updateVendorWithDashboardId={updateVendorWithDashboardId}
              allVendors={allVendors}
              canEdit={canEdit}
            />
          ))}
        {isFetching && searchParams.page > 1 && (
          <ListGroup.Item>
            <div className="text-muted text-center">
              <Spinner animation="border" variant="warning" size="sm" />
              <span className="ms-2">Loading more...</span>
            </div>
          </ListGroup.Item>
        )}
        {displayVendors.length > 0 && hasMoreResults && !isFetching && (
          <ListGroup.Item className="text-center">
            <small className="text-muted">Scroll down for more results</small>
          </ListGroup.Item>
        )}
        {displayVendors.length > 0 && !hasMoreResults && !isFetching && (
          <ListGroup.Item className="text-center">
            <small className="text-muted">No more results</small>
          </ListGroup.Item>
        )}
        {displayVendors.length === 0 && searchParams.search !== '' && !isLoading && !isFetching && (
          <ListGroup.Item className="text-center">
            <small className="text-muted">No results found</small>
          </ListGroup.Item>
        )}
      </ListGroup>
      {canEdit && (displayVendors.length > 0 || allVendors.length > 0) && (
        <div className="d-flex gap-2 mt-2">
          {displayVendors.length > 0 && hasMoreResults && !loadAll && (
            <Button
              variant="light-primary"
              size="sm"
              onClick={() => {
                setLoadAll(true)
                setSearchParams((prev) => ({ ...prev, perPage: 1000, page: 1 }))
                setAccumulatedVendors([])
              }}
            >
              Load All
            </Button>
          )}
          {displayVendors.length > 1 && ['include', 'exclude'].map((option) => (
            <Button
              key={option}
              variant={option === 'include' ? 'outline-primary' : 'outline-warning'}
              size="sm"
              onClick={() => {
                const newVendors = displayVendors.map((v) => ({
                  ...pick(v, ['id', 'name', 'external_id']),
                  options: option,
                  dashboard_vendor_id: null
                }))
                setParams((prev) => ({
                  ...prev,
                  vendors: option === 'include'
                    ? [...(prev.vendors || []), ...newVendors]
                    : prev.vendors || [],
                  excludedVendors: option === 'exclude'
                    ? [...(prev.excludedVendors || []), ...newVendors]
                    : prev.excludedVendors || []
                }))
                if (params.dashboardId) {
                  bulkCreateVendors({
                    dashboardId: params.dashboardId,
                    vendorIds: displayVendors.map((v) => v.id),
                    options: option
                  })
                }
                queryClient.invalidateQueries('transactions')
                setSearchParams(defaultSearchParams)
                setAccumulatedVendors([])
                setHasMoreResults(true)
                setLoadAll(false)
              }}
            >
              {option === 'include' ? 'Include' : 'Exclude'} All ({displayVendors.length})
            </Button>
          ))}
          {allVendors.length > 0 && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => {
                if (params.dashboardId) {
                  bulkDestroyVendors(params.dashboardId)
                }
                setParams((prev) => ({
                  ...prev,
                  vendors: [],
                  excludedVendors: []
                }))
                queryClient.invalidateQueries('transactions')
              }}
            >
              Clear All ({allVendors.length})
            </Button>
          )}
        </div>
      )}

      <Table size="sm" className="mt-4">
        <thead>
          <tr>
            <th>Name</th>
            <th></th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {allVendors.length === 0 && (
            <tr>
              <td colSpan="3" className="text-center">
                No vendors selected. Search for and select a vendor above to include or
                exclude from the view.
              </td>
            </tr>
          )}
          {allVendors.map((vendor) => (
            <tr key={`dashboard-vendor-${vendor.id}`}>
              <td>
                <div className="mt-1">{vendor.name}
                  <small className="text-muted ms-2">(ID: {vendor.external_id})</small>
                </div>
              </td>
              <td>
                <UpdateVendor
                  toggleIncludeExclude={toggleIncludeExclude}
                  vendor={vendor}
                  dashboardId={params.dashboardId}
                  canEdit={canEdit}
                />
              </td>
              <td>
                <DeleteVendor
                  addOrRemoveVendor={addOrRemoveVendor}
                  vendor={vendor}
                  dashboardId={params.dashboardId}
                  canEdit={canEdit}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )

  if (showAsAccordion) {
    return (
      <div className="mb-2">
        {/* Accordion Header */}
        <div
          className={`d-flex justify-content-between align-items-center p-3 border rounded ${
            isRequired ? 'border-warning' : ''
          }`}
          style={{ cursor: 'pointer', backgroundColor: isExpanded ? '#f8f9fa' : 'white' }}
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="d-flex align-items-center gap-3 flex-grow-1">
            <div className="d-flex align-items-center gap-2">
              <strong>{label}</strong>
              {isRequired && (
                <Badge bg="warning" text="dark" className="small">
                  Required
                </Badge>
              )}
            </div>
            {!isExpanded && (
              <div className="text-muted small">{formatVendorValue()}</div>
            )}
          </div>
          <FontAwesomeIcon icon={isExpanded ? faChevronDown : faChevronRight} className="text-muted" />
        </div>

        {/* Accordion Content */}
        {isExpanded && (
          <div className="p-3 border border-top-0 rounded-bottom">
            {content}
          </div>
        )}
      </div>
    )
  }

  return content
}

export default VendorSearch
